import React,{useEffect,useMemo,useRef,useState} from 'react'
import axios from 'axios'
import {useNavigate,useParams} from 'react-router-dom'
import { MultiSelect } from "react-multi-select-component";
import moment from 'moment';
import DataTable from 'react-data-table-component';
import exportFromJSON from 'export-from-json'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import PlayerCard from '../ui/PlayerCard'
import './createlist.css'

/* ============================================================
   CREATE LIST — the scouting search.

   The nine criteria are grouped the way a scout narrows a squad:
     WHERE     country (required)
     WHO       positions, age window
     PHYSICAL  height, weight, preferred foot/hand
     NAME      free text

   Only the presentation and the feedback changed. createSQL() assembles
   exactly the same string, posts the same payload to the same endpoint,
   and filters the response on age and name exactly as before.
   ============================================================ */

/* ------------------------------------------------------------------
   ICONS — inline so they inherit currentColor from the token palette.
   ------------------------------------------------------------------ */
const IconSearch=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-4.2-4.2"/></svg>
)
const IconSave=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h11l3 3v13H5z"/><path d="M9 4v5h6V4"/><path d="M9 20v-6h6v6"/></svg>
)
const IconDownload=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v10"/><path d="M8 11l4 4 4-4"/><path d="M5 19h14"/></svg>
)
const IconOpen=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="2.6"/></svg>
)
const IconX=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
)

/* ------------------------------------------------------------------
   PREFERRED FOOT / HAND
   Values are the same strings the old <select> produced: '0'..'3',
   and createSQL still reads them through  if (o>0) -> LeftRight=o.
   ------------------------------------------------------------------ */
const SIDES=[
  {value:'0', label:'Any'},
  {value:'1', label:'Right'},
  {value:'2', label:'Left'},
  {value:'3', label:'Both'}
]

const SIDE_LABEL={
  '1':'Right handed / footed',
  '2':'Left handed / footed',
  '3':'Both left & right'
}

/* A criterion is "off" when it is 0 — the whole search is built on that.
   The UI therefore shows an empty box, never a zero that reads like a bound. */
const isNoLimit=(value)=>{
  if (value===null||value===undefined) return true
  const text=String(value).trim()
  if (text==='') return true
  const n=Number(text)
  return !isNaN(n) && n===0
}

const asNumber=(value)=>{
  const n=Number(value)
  return isNaN(n)?0:n
}

/* Height is typed in metres and matched as centimetres (h1*100 in the SQL). */
const toCm=(value)=>Math.round(asNumber(value)*100)
const metresText=(value)=>asNumber(value).toFixed(2)+' m'

const plural=(n,one,many)=>(n===1?one:many)

/* ------------------------------------------------------------------
   NUMBER FIELD — one half of a from/to pair.
   The draft string lets someone type "0.5" without the leading zero
   being swallowed, while an unfocused 0 always shows as "No limit".
   ------------------------------------------------------------------ */
function NumField({inputId,label,unit,value,onChange,min,step}){
  const [draft,setDraft]=useState(null)
  const shown=draft!==null?draft:(isNoLimit(value)?'':String(value))

  return (
    <span className="cl-num">
      <input
        id={inputId}
        className="cl-input"
        type="number"
        min={min}
        step={step}
        placeholder="No limit"
        aria-label={label}
        value={shown}
        onFocus={()=>setDraft(isNoLimit(value)?'':String(value))}
        onBlur={()=>setDraft(null)}
        onChange={(e)=>{
          setDraft(e.target.value)
          /* an emptied box is the "no limit" 0 the query builder expects */
          onChange(e.target.value===''?0:e.target.value)
        }}
      />
      <span className="cl-unit" aria-hidden="true">{unit}</span>
    </span>
  )
}

/* ------------------------------------------------------------------
   THEME TOKENS FOR react-data-table-component
   The table takes colours as JS values, so they are read back off the
   root element and re-read whenever the theme flips (the toggle stamps
   data-theme on <html>) or the OS preference changes.
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{
    out[name]=String(styles.getPropertyValue(name)||'').trim()
  })
  return out
}

function useThemeTokens(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      /* read after the new custom properties have landed */
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return tokens
}

export default function CreateList() {
    const navigate=useNavigate ()
    const [countries,setCountries]=useState([])//useState(JSON.parse(sessionStorage.getItem('appCountries')))
    const [appPositions,setAppPositions]=useState([])
    const [positions,setPositions]=useState([])
    const [queryRsults,setQueryResults]=useState([])
    const [isLoading,setIsLoading]=useState(false)
    const [data,setData]=useState([])
    const [a1,setA1]=useState(0)
    const [a2,setA2]=useState(0)
    const [h1,setH1]=useState(0)
    const [h2,setH2]=useState(0)
    const [w1,setW1]=useState(0)
    const [w2,setW2]=useState(0)
    const [o,setO]=useState(0)
    const [s,setS]=useState('')
    const [listName,setListName]=useState('Create New List')
    const [sqlCriteria,setSqlCriteria]=useState('')
    const [show,setShow]=useState(false)
    const resultsTbl=useRef()

    /* feedback state — this is what replaced the alert() calls */
    const [isSearching,setIsSearching]=useState(false)
    const [searchFailed,setSearchFailed]=useState(false)
    const [hasRun,setHasRun]=useState(false)
    const [ranSignature,setRanSignature]=useState('')
    const [note,setNote]=useState(null)
    const [saveNote,setSaveNote]=useState(null)
    const [confirmDel,setConfirmDel]=useState(false)

    const {listID}=useParams()

    const tokens=useThemeTokens()

    const openPlayer=(uuid)=>navigate('/user/'+uuid)

    const columns = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width: '58px',
            center: true
        },
        {
            name: '',
            cell: row => (
                <button
                    type="button"
                    className="cl-rowbtn"
                    title="Open player profile"
                    aria-label="Open player profile"
                    onClick={()=>openPlayer(row.UUID)}
                >
                    <IconOpen/>
                </button>
            ),
            width: '58px',
            center: true
        },
        {
            name: 'Name',
            selector: (row,index) =>  row.FirstName+ ' '+row.LastName,
            cell: row => (
                <PlayerCard
                    uuid={row.UUID}
                    name={row.FirstName+' '+row.LastName}
                    position={row.Position}
                    points={row.Points}
                >
                    <span className="cl-name">{row.FirstName+' '+row.LastName}</span>
                </PlayerCard>
            ),
            sortable: true,
            minWidth: '190px',
            grow: 2
        },
        {
            name: 'Age',
            selector: (row,index) =>  row.Age,
            sortable: true,
            width: '76px',
            center: true
        },
        {
            name: 'Country',
            selector: (row,index) =>  row.Country,
            sortable: true,
            minWidth: '120px'
        },
        {
            name: 'Position',
            selector: (row,index) =>  row.Position,
            sortable: true,
            minWidth: '140px'
        },
        {
            name: 'Orientation',
            selector: (row,index) =>  row.LeftRight==1 ? 'Right' : row.LeftRight==2 ?  'Left' : 'Both Left & Right' ,
            sortable: true,
            minWidth: '150px'
        },
        {
            name: 'Height',
            selector: (row,index) =>  row.Height,
            sortable: true,
            width: '92px',
            center: true
        },
        {
            name: 'Weight',
            selector: (row,index) =>  row.Weight,
            sortable: true,
            width: '92px',
            center: true
        },
        {
            name: 'Team',
            selector: (row,index) =>  row.Team,
            sortable: true,
            minWidth: '150px'
        } ,
        {
            name: 'Points',
            selector: (row,index) =>  row.Points,
            sortable: true,
            width: '86px',
            center: true
        },
        {
            name: 'Posts',
            selector: (row,index) =>  row.Posts,
            sortable: true,
            width: '86px',
            center: true
        },
        {
            name: 'Challenges',
            selector: (row,index) =>  row.Challenges,
            sortable: true,
            width: '112px',
            center: true
        },

    ]

    /* Every colour below comes from theme/tokens.css via getComputedStyle,
       so the table repaints with the rest of the product on a theme change. */
    const tableStyles=useMemo(()=>{
      const t=tokens
      return {
        table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
        tableWrapper:{ style:{ display:'block' } },
        head:{ style:{ color:t['--ink-3'] } },
        headRow:{
          style:{
            backgroundColor:t['--panel-raise'],
            color:t['--ink-3'],
            minHeight:'42px',
            borderBottomWidth:'1px',
            borderBottomColor:t['--line'],
            borderBottomStyle:'solid'
          }
        },
        headCells:{
          style:{
            paddingLeft:'12px',
            paddingRight:'12px',
            fontFamily:t['--mono'],
            fontSize:'10px',
            fontWeight:600,
            letterSpacing:'0.08em',
            textTransform:'uppercase',
            color:t['--ink-3']
          }
        },
        rows:{
          style:{
            backgroundColor:t['--panel'],
            color:t['--ink'],
            fontFamily:t['--sans'],
            fontSize:t['--step-0']||'0.875rem',
            minHeight:'46px',
            '&:not(:last-of-type)':{
              borderBottomWidth:'1px',
              borderBottomStyle:'solid',
              borderBottomColor:t['--line-soft']
            }
          },
          highlightOnHoverStyle:{
            backgroundColor:t['--accent-wash'],
            color:t['--ink'],
            borderBottomColor:t['--line-soft'],
            outlineWidth:'0px',
            outlineColor:t['--panel'],
            transitionDuration:'0.15s',
            transitionProperty:'background-color'
          }
        },
        cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
        pagination:{
          style:{
            backgroundColor:t['--panel'],
            color:t['--ink-3'],
            fontSize:'12px',
            minHeight:'50px',
            borderTopWidth:'1px',
            borderTopStyle:'solid',
            borderTopColor:t['--line']
          },
          pageButtonsStyle:{
            color:t['--ink-2'],
            fill:t['--ink-2'],
            backgroundColor:'transparent',
            '&:disabled':{ color:t['--ink-3'], fill:t['--ink-3'] },
            '&:hover:not(:disabled)':{ backgroundColor:t['--panel-raise'] },
            '&:focus':{ outline:'none', backgroundColor:t['--accent-wash'] }
          }
        },
        noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'], padding:'26px' } },
        progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
      }
    },[tokens])

    const fileName = 'list'+Date.now()
    const exportType = 'xls'

    const   ExportToExcel = () => {
      const excelResults=queryRsults.map(item=>{
        return(
            {
                Name:item.FirstName+ ' '+item.LastName,
                Age:item.Age,
                Country:item.Country,
                Position:item.Position,
                Orientation:item.LeftRight==1 ? 'Right' : item.LeftRight==2 ?  'Left' : 'Both Left & Right',
                Height:item.Height,
                Weight:item.Weight,
                Points:item.Points,
                NumberOfPosts:item.Posts,
                NumberOfChallenges:item.Challenges


            }
        )
      })
      exportFromJSON({ data:excelResults, fileName, exportType })
    }

    const handleShow=()=>{
        setSaveNote(null)
        setShow(true)
    }

    const handleClose=()=>{
        setSaveNote(null)
        setShow(false)
    }

    useEffect(()=>{
        loadPositions()
        if (listID){
            loadList()
        }
    },[])

    /* Esc closes the save dialog. */
    useEffect(()=>{
        if (!show) return undefined
        const onKey=(ev)=>{ if (ev.key==='Escape') setShow(false) }
        window.addEventListener('keydown',onKey)
        return ()=>window.removeEventListener('keydown',onKey)
    },[show])

    const loadList= ()=>{

        setIsLoading(true)
        axios.get(process.env.REACT_APP_API_SERVER+'manager/listscriteria/list/'+listID,{
                headers: {
                    'Authorization': 'Bearer '+sessionStorage.getItem("mngapiToken")
                    }
                })
            .then(res=>{
                if (res.data.err==0){

                setCountries(res.data.results.countries)
                setPositions(res.data.results.positions)
                    setListName(res.data.results.listName)
                    setA1(res.data.results.a1)
                    setA2(res.data.results.a2)
                    setH1(res.data.results.h1)
                    setH2(res.data.results.h2)
                    setW1(res.data.results.w1)
                    setW2(res.data.results.w2)
                    setO(res.data.results.o)
                    setS(res.data.results.s)

                }else{
                setNote({tone:'crit',text:'This list could not be loaded. Please go back and open it again.'})
                }

                setIsLoading(false)

            }).catch(err=>{
                setIsLoading(false)
                setNote({tone:'crit',text:'This list could not be loaded. Check your connection and try again.'})
                console.log(err)
            })

      }


    const loadPositions=()=>{
        setIsLoading(true)
        axios.get(process.env.REACT_APP_API_SERVER+'manager/listscriteria/positions/'+sessionStorage.getItem('SportID'),{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("mngapiToken")
                }
            })
          .then(res=>{

            setAppPositions(res.data.results)
            setIsLoading(false)

          }).catch(err=>{
              setIsLoading(false)
              setNote({tone:'crit',text:'The position list could not be loaded, so that filter is empty right now.'})
              console.log(err)
          })
    }

    const positionsList=appPositions.map(item=>{
        return(
            {label:item.Title, value:item.PositionID}
        )
    })

    /* sessionStorage can be empty or stale — never hand MultiSelect a null. */
    const countryOptions=useMemo(()=>{
      try{
        const raw=sessionStorage.getItem('appCountries')
        const parsed=raw?JSON.parse(raw):null
        return Array.isArray(parsed)?parsed:[]
      }catch(err){
        return []
      }
    },[])

    /* ------------------------------------------------------------------
       LIVE SUMMARY — one removable chip per active criterion.
       Clearing a chip only edits the criteria; the results below stay as
       they were until View Results runs again (hence the "stale" flag).
       ------------------------------------------------------------------ */
    const chips=[]

    countries.forEach(item=>{
      chips.push({
        id:'country-'+item.value,
        k:'Country',
        v:item.label,
        clear:()=>setCountries(countries.filter(other=>other.value!==item.value))
      })
    })

    positions.forEach(item=>{
      chips.push({
        id:'position-'+item.value,
        k:'Position',
        v:item.label,
        clear:()=>setPositions(positions.filter(other=>other.value!==item.value))
      })
    })

    if (!isNoLimit(a1)) chips.push({id:'a1',k:'Age from',v:asNumber(a1)+' yrs',clear:()=>setA1(0)})
    if (!isNoLimit(a2)) chips.push({id:'a2',k:'Age to',v:asNumber(a2)+' yrs',clear:()=>setA2(0)})
    if (!isNoLimit(h1)) chips.push({id:'h1',k:'Height from',v:metresText(h1),clear:()=>setH1(0)})
    if (!isNoLimit(h2)) chips.push({id:'h2',k:'Height to',v:metresText(h2),clear:()=>setH2(0)})
    if (!isNoLimit(w1)) chips.push({id:'w1',k:'Weight from',v:asNumber(w1)+' kg',clear:()=>setW1(0)})
    if (!isNoLimit(w2)) chips.push({id:'w2',k:'Weight to',v:asNumber(w2)+' kg',clear:()=>setW2(0)})
    if (!isNoLimit(o))  chips.push({id:'o',k:'Preferred',v:SIDE_LABEL[String(o)]||'Any',clear:()=>setO(0)})
    if (String(s).length>0) chips.push({id:'s',k:'Name',v:String(s),clear:()=>setS('')})

    const clearAll=()=>{
      setCountries([])
      setPositions([])
      setA1(0); setA2(0)
      setH1(0); setH2(0)
      setW1(0); setW2(0)
      setO(0)
      setS('')
    }

    /* What the last search was run with, so a changed criterion is visible. */
    const signature=JSON.stringify([
      countries.map(item=>item.value),
      positions.map(item=>item.value),
      String(a1),String(a2),String(h1),String(h2),String(w1),String(w2),String(o),String(s)
    ])

    const isStale=hasRun && !isSearching && ranSignature!==signature

    const rangeHint=(from,to,unit,noneText)=>{
      const hasFrom=!isNoLimit(from)
      const hasTo=!isNoLimit(to)
      if (!hasFrom && !hasTo) return noneText
      if (hasFrom && hasTo) return 'Matches '+from+'–'+to+' '+unit+'.'
      if (hasFrom) return 'Matches '+from+' '+unit+' and above.'
      return 'Matches up to '+to+' '+unit+'.'
    }

    /* Height is typed in metres; the query compares centimetres. Spell that out. */
    const heightHint=()=>{
      const hasFrom=!isNoLimit(h1)
      const hasTo=!isNoLimit(h2)
      if (!hasFrom && !hasTo) return 'Any height.'
      if (hasFrom && hasTo) return 'Matches '+toCm(h1)+'–'+toCm(h2)+' cm.'
      if (hasFrom) return 'Matches '+toCm(h1)+' cm and above.'
      return 'Matches up to '+toCm(h2)+' cm.'
    }

    const scrollToResults=()=>{
      if (resultsTbl.current && resultsTbl.current.scrollIntoView){
        resultsTbl.current.scrollIntoView({behavior:'smooth',block:'start'})
      }
    }

    /* ------------------------------------------------------------------
       SEAM — natural-language search.
       A future backend route (e.g. manager/listscriteria/parse) would take a
       plain sentence, return these same nine values, and push them through the
       setters above; createSQL() below would then run untouched. Nothing on
       this screen calls a model and no such endpoint exists yet, so nothing
       is wired up here.
       ------------------------------------------------------------------ */

    const createSQL=()=>{
        var sqlCountries=''
        var sql=''
        const SportID=sessionStorage.getItem('SportID')

        countries.forEach((item,index)=>{
            if (index==0){
                sqlCountries=sqlCountries+ ' CountryIDINT='+item.value
            }else{  
                sqlCountries=sqlCountries+ ' OR CountryIDINT='+item.value
            }
        })
        if (sqlCountries.length>0){
            sqlCountries='('+sqlCountries+')'
        }
        sql=sqlCountries

        var sqlPositions=''
        positions.forEach((item,index)=>{
            if (index==0){
                sqlPositions=sqlPositions+ ' Users.PositionID='+item.value
            }else{  
                sqlPositions=sqlPositions+ ' OR Users.PositionID='+item.value
            }
        })
        if (sqlPositions.length>0){
            sqlPositions='('+sqlPositions+')'
        }
        if (sql.length>0 && sqlPositions.length>0){
            sql=sql+' AND '+sqlPositions
        }else{
            sql=sql+sqlPositions
        }
        
        var sqlHeight1=''
        if (h1>0){
            sqlHeight1=' Height>='+h1*100
        }
        if (sql.length>0 && sqlHeight1.length>0){
            sql=sql+' AND '+sqlHeight1
        }else{
            sql=sql+sqlHeight1
        }        

        var sqlHeight2=''
        if (h2>0){
            sqlHeight2=' Height<='+h2*100
        }
        if (sql.length>0 && sqlHeight2.length>0){
            sql=sql+' AND '+sqlHeight2
        }else{
            sql=sql+sqlHeight2
        }  


        var sqlWeight1=''
        if (w1>0){
            sqlWeight1=' Weight>='+w1
        }
        if (sql.length>0 && sqlWeight1.length>0){
            sql=sql+' AND '+sqlWeight1
        }else{
            sql=sql+sqlWeight1
        }        

        var sqlWeight2=''
        if (w2>0){
            sqlWeight2=' Weight<='+w2
        }
        if (sql.length>0 && sqlWeight2.length>0){
            sql=sql+' AND '+sqlWeight2
        }else{
            sql=sql+sqlWeight2
        }  


        var sqlOrientation=''
        if (o>0){
            sqlOrientation=' LeftRight='+o
        }
        if (sql.length>0 && sqlOrientation.length>0){
            sql=sql+' AND '+sqlOrientation
        }else{
            sql=sql+sqlOrientation
        }  



        setSqlCriteria(sql)

        setNote(null)
        setSearchFailed(false)
        setIsSearching(true)
        setHasRun(true)
        setRanSignature(signature)

        axios.post(process.env.REACT_APP_API_SERVER+'manager/listscriteria/results',{sql,SportID},{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("mngapiToken")
                }            
            })
          .then(res=>{
            let listResults=res.data.results
            let results=res.data.results
            const todayDate = moment().year()
            
            results.forEach(item=>{
                const dateOfBirth=moment(item.DateOfBirth).year()
                item.Age=todayDate-dateOfBirth
                //console.log(todayDate-dateOfBirth)
            })
            
            if (a1>0){
                listResults=listResults.filter(item=>item.Age>=a1)
                //results=[...listResults]
            }
            if (a2>0){
                listResults=listResults.filter(item=>item.Age<=a2)
                //results=[...listResults]
            }
            if (s.length>0){
                listResults=listResults.filter(item=>item.SearchStr.includes(s.toUpperCase()))
                //results=[...listResults]
            }
            //console.log(listResults)


            if (listResults.length>0){
                setQueryResults(listResults)
            }else{
                setQueryResults([])
            }

            setIsSearching(false)
            scrollToResults()

          }).catch(err=>{
            setIsSearching(false)
            setSearchFailed(true)
            setQueryResults([])
          })
    }

    const saveList=(e)=>{
        e.preventDefault()

        const listObj={
            id:listID ? listID : 0,
            countries:JSON.stringify(countries),
            positions:JSON.stringify(positions),
            a1,
            a2,
            h1,
            h2,
            w1,
            w2,
            s,
            o,
            listName,
            uid:sessionStorage.getItem('userToken')
        }


        axios.post(process.env.REACT_APP_API_SERVER+'manager/listscriteria/lists',listObj,{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("mngapiToken")
                }
            })
          .then(res=>{

           if (res.data.err==0){
            setShow(false)
            navigate('/lists')
           }else{
            setSaveNote('This list could not be saved. Please try again.')
           }

          }).catch(err=>{
            setSaveNote('This list could not be saved. Check your connection and try again.')
          })

    }

    const delList=()=>{
        setConfirmDel(false)
        setIsLoading(true)
        axios.delete(process.env.REACT_APP_API_SERVER+'manager/listscriteria/list/'+listID,{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("mngapiToken")
                }
            })
          .then(res=>{
            if (res.data.err==0){

                navigate('/lists')
            }else{
              setNote({tone:'crit',text:'This list could not be deleted. Please try again.'})
            }

            setIsLoading(false)

          }).catch(err=>{
              setIsLoading(false)
              setNote({tone:'crit',text:'This list could not be deleted. Check your connection and try again.'})
              console.log(err)
          })
    }

    const noCountry=countries.length==0
    const resultCount=queryRsults.length
    const nameText=(s===null||s===undefined)?'':String(s)

  return (

    <div className="cl-page">

      <div className="cl-head">
        <div className="cl-headline">
          <span className="mono cl-eyebrow">{listID?'Saved list':'New search'}</span>
          {/* On a new search the shell's TopBar already reads "Create List";
              the heading only earns its place when it names a saved list. */}
          {listID && <h2 className="cl-title">{listName}</h2>}
          <div className="cl-head-sub">
            Narrow the pool by where a player plays, who they are, and how they are built.
            Country is the only thing the search cannot run without.
          </div>
        </div>

        <div className="cl-head-actions">
          {listID
            ? (confirmDel
                ? <span className="cl-confirm">
                    <span className="cl-confirm-t">Delete this list?</span>
                    <Btn onClick={()=>setConfirmDel(false)}>Keep it</Btn>
                    <Btn className="cl-danger" onClick={delList}>Delete</Btn>
                  </span>
                : <Btn onClick={()=>setConfirmDel(true)}>Delete List</Btn>)
            : null}
          <Btn onClick={()=>navigate('/lists')}>View / Edit Lists</Btn>
        </div>
      </div>

      {isLoading
        ? <div className="cl-note" data-tone="info" role="status"><span>Loading…</span></div>
        : null}

      {note
        ? <div className="cl-note" data-tone={note.tone} role="status">
            <span>{note.text}</span>
            <button type="button" className="cl-note-x" aria-label="Dismiss" onClick={()=>setNote(null)}><IconX/></button>
          </div>
        : null}

      {/* ---------------- CRITERIA ---------------- */}
      <div className="cl-criteria">

        <Panel
          className="cl-panel"
          title="Country"
          subtitle="Where you are scouting. The search will not run without at least one."
          actions={<span className="cl-sec mono">Where</span>}
        >
          <div className="cl-field">
            <span className="mono cl-label">Countries</span>
            <MultiSelect
              hasSelectAll={false}
              options={countryOptions}
              value={countries}
              onChange={setCountries}
              labelledBy="Select"
              overrideStrings={{
                selectSomeItems:'Choose one or more countries…',
                allItemsAreSelected:'Every country selected',
                search:'Search countries'
              }}
            />
            <div className="cl-hint">
              {noCountry
                ? <span className="cl-req">Required — pick a country to unlock the search.</span>
                : countries.length+' '+plural(countries.length,'country','countries')+' selected.'}
            </div>
          </div>
        </Panel>

        <Panel
          className="cl-panel"
          title={'Position & age'}
          subtitle="The role you are recruiting for and the age window that fits it."
          actions={<span className="cl-sec mono">Who</span>}
        >
          <div className="cl-field">
            <span className="mono cl-label">Positions</span>
            <MultiSelect
              hasSelectAll={false}
              options={positionsList}
              value={positions}
              onChange={setPositions}
              labelledBy="Select"
              overrideStrings={{
                selectSomeItems:'Any position',
                allItemsAreSelected:'Every position selected',
                search:'Search positions'
              }}
            />
            <div className="cl-hint">
              {positions.length===0
                ? 'Left empty, every position is included.'
                : positions.length+' '+plural(positions.length,'position','positions')+' selected.'}
            </div>
          </div>

          <div className="cl-field">
            <label className="mono cl-label" htmlFor="cl-a1">Age range</label>
            <div className="cl-range">
              <NumField inputId="cl-a1" label="Age from" unit="yrs" value={a1} onChange={setA1} min={11} step={1}/>
              <span className="cl-range-sep">to</span>
              <NumField inputId="cl-a2" label="Age to" unit="yrs" value={a2} onChange={setA2} min={11} step={1}/>
            </div>
            <div className="cl-hint">{rangeHint(a1,a2,'years old','Any age — an empty box means no limit.')}</div>
          </div>
        </Panel>

        <Panel
          className="cl-panel"
          title={'Height, weight & preferred side'}
          subtitle="An empty box means no limit on that end of the range."
          actions={<span className="cl-sec mono">Physical</span>}
        >
          <div className="cl-field">
            <label className="mono cl-label" htmlFor="cl-h1">Height range</label>
            <div className="cl-range">
              <NumField inputId="cl-h1" label="Height from, in metres" unit="m" value={h1} onChange={setH1} min={0} step={0.1}/>
              <span className="cl-range-sep">to</span>
              <NumField inputId="cl-h2" label="Height to, in metres" unit="m" value={h2} onChange={setH2} min={0} step={0.1}/>
            </div>
            <div className="cl-hint">Typed in metres — <b>1.80</b> is 180 cm. {heightHint()}</div>
          </div>

          <div className="cl-field">
            <label className="mono cl-label" htmlFor="cl-w1">Weight range</label>
            <div className="cl-range">
              <NumField inputId="cl-w1" label="Weight from" unit="kg" value={w1} onChange={setW1} min={0} step={1}/>
              <span className="cl-range-sep">to</span>
              <NumField inputId="cl-w2" label="Weight to" unit="kg" value={w2} onChange={setW2} min={0} step={1}/>
            </div>
            <div className="cl-hint">{rangeHint(w1,w2,'kg','Any weight.')}</div>
          </div>

          <div className="cl-field">
            <span className="mono cl-label">Preferred foot / hand</span>
            <div className="cl-seg" role="group" aria-label="Preferred foot or hand">
              {SIDES.map(side=>(
                <button
                  key={side.value}
                  type="button"
                  className="toggle"
                  aria-pressed={String(o)===side.value}
                  onClick={()=>setO(side.value)}
                >
                  {side.label}
                </button>
              ))}
            </div>
            <div className="cl-hint">
              {isNoLimit(o) ? 'Any side.' : 'Matches '+(SIDE_LABEL[String(o)]||'any')+' players.'}
            </div>
          </div>
        </Panel>

        <Panel
          className="cl-panel"
          title="Name or nickname"
          subtitle="Free text, for when you already know who you are looking for."
          actions={<span className="cl-sec mono">Name</span>}
        >
          <div className="cl-field">
            <label className="mono cl-label" htmlFor="cl-s">Search text</label>
            <input
              id="cl-s"
              className="cl-input"
              type="text"
              maxLength={50}
              placeholder="Any name"
              value={nameText}
              onChange={(e)=>setS(e.target.value)}
            />
            <div className="cl-hint">
              {nameText.length>0
                ? <span>Keeps only players whose name or nickname contains <b>{nameText}</b>.</span>
                : 'Leave it empty to search on the other criteria alone.'}
            </div>
          </div>

          <div className="cl-hint">
            This one is applied to the results after they come back, so it narrows what the
            rest of the search already found rather than widening it.
          </div>
        </Panel>

      </div>

      {/* ---------------- LIVE SUMMARY + ACTIONS ---------------- */}
      <div className="cl-bar">
        <div className="cl-bar-crit">
          <span className="mono cl-bar-label">
            Active criteria
            <span className="cl-bar-count num">{chips.length}</span>
            {chips.length>0
              ? <button type="button" className="toggle" onClick={clearAll}>Clear all</button>
              : null}
          </span>

          {chips.length>0
            ? <div className="cl-chips">
                {chips.map(chip=>(
                  <span className="cl-chip" key={chip.id}>
                    <span className="cl-chip-k">{chip.k}</span>
                    <span className="cl-chip-v" title={chip.k+': '+chip.v}>{chip.v}</span>
                    <button
                      type="button"
                      className="cl-chip-x"
                      aria-label={'Remove '+chip.k+' '+chip.v}
                      onClick={chip.clear}
                    >
                      <IconX/>
                    </button>
                  </span>
                ))}
              </div>
            : <div className="cl-chips-none">Nothing set yet — start with a country above.</div>}
        </div>

        <div className="cl-bar-actions">
          <Btn onClick={createSQL} disabled={countries.length==0}><IconSearch/>View Results</Btn>
          <Btn variant="accent" onClick={handleShow} disabled={countries.length==0}><IconSave/>Save List</Btn>
        </div>

        {noCountry
          ? <div className="cl-blocked"><b>Country is required.</b> Both actions unlock as soon as one is picked.</div>
          : null}
      </div>

      {/* ---------------- RESULTS ---------------- */}
      <div ref={resultsTbl}>
        <Panel
          className="cl-results"
          title="Results"
          subtitle={
            isSearching ? 'Running the search…'
            : searchFailed ? 'The search did not complete'
            : hasRun ? resultCount+' '+plural(resultCount,'player','players')+' returned'
            : 'Nothing run yet'
          }
          actions={resultCount>0 && !isSearching
            ? <Btn onClick={ExportToExcel}><IconDownload/>Export to Excel</Btn>
            : null}
        >
          {isStale
            ? <div className="cl-note" data-tone="info" role="status">
                <span>The criteria changed since this search ran — choose <b>View Results</b> to refresh the table.</span>
              </div>
            : null}

          {isSearching
            ? <div className="cl-sk" aria-hidden="true">
                <span className="skeleton"></span>
                <span className="skeleton"></span>
                <span className="skeleton"></span>
                <span className="skeleton"></span>
                <span className="skeleton"></span>
              </div>
            : searchFailed
              ? <div className="cl-empty">
                  <div className="cl-empty-t">The search could not be completed</div>
                  <div className="cl-empty-s">
                    Something went wrong between here and the server. Your criteria are untouched —
                    run it again and it will usually go through.
                  </div>
                  <div className="cl-empty-a">
                    <Btn onClick={createSQL} disabled={countries.length==0}><IconSearch/>Try again</Btn>
                  </div>
                </div>
              : !hasRun
                ? <div className="cl-empty">
                    <div className="cl-empty-t">No search run yet</div>
                    <div className="cl-empty-s">
                      Set your criteria above and choose View Results. Matches land here as a
                      sortable table you can open, preview and export.
                    </div>
                  </div>
                : resultCount===0
                  ? <div className="cl-empty">
                      <div className="cl-empty-t">No players match these criteria</div>
                      <div className="cl-empty-s">
                        Every filter narrows the pool. Widening the age or height range, or adding
                        a second country, is usually enough to bring players back.
                      </div>
                      <div className="cl-empty-a">
                        <Btn onClick={clearAll}>Clear all criteria</Btn>
                      </div>
                    </div>
                  : <>
                      <div className="cl-count">
                        <span className="cl-count-n num">{resultCount}</span>
                        <span className="cl-count-l">
                          {plural(resultCount,'player matches','players match')} these criteria.
                        </span>
                      </div>

                      <div className="cl-table">
                        <DataTable
                          columns={columns}
                          data={queryRsults}
                          customStyles={tableStyles}
                          highlightOnHover
                          persistTableHead
                          pagination
                          paginationPerPage={25}
                          paginationRowsPerPageOptions={[25,50,100,200]}
                        />
                      </div>
                    </>}
        </Panel>
      </div>

      {/* ---------------- SAVE DIALOG ---------------- */}
      {show
        ? <div className="cl-modal" role="dialog" aria-modal="true" aria-labelledby="cl-save-title">
            <div className="cl-modal-panel">
              <div className="cl-modal-head">
                <div>
                  <div className="cl-modal-title" id="cl-save-title">Save this list</div>
                  <div className="cl-modal-sub">
                    {listID
                      ? 'Updates the saved list with the criteria above.'
                      : 'Stores the criteria above so you can run them again later.'}
                  </div>
                </div>
                <button type="button" className="cl-modal-x" aria-label="Close" onClick={handleClose}>
                  <IconX/>
                </button>
              </div>

              {saveNote
                ? <div className="cl-note" data-tone="crit" role="alert"><span>{saveNote}</span></div>
                : null}

              <form className="cl-modal-form" onSubmit={saveList}>
                <div className="cl-field">
                  <label className="mono cl-label" htmlFor="cl-listname">List name</label>
                  <input
                    id="cl-listname"
                    className="cl-input"
                    required
                    type="text"
                    maxLength={50}
                    value={listName}
                    onChange={(e)=>setListName(e.target.value)}
                  />
                  <div className="cl-hint">
                    {chips.length+' '+plural(chips.length,'criterion','criteria')+' will be stored with this list.'}
                  </div>
                </div>

                <div className="cl-modal-foot">
                  <Btn onClick={handleClose}>Cancel</Btn>
                  <Btn variant="accent" type="submit"><IconSave/>Save List</Btn>
                </div>
              </form>
            </div>
          </div>
        : null}

    </div>
  )
}
